import { Box, Divider, IconButton, Popover, Skeleton, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DoNotDisturbAltOutlinedIcon from "@mui/icons-material/DoNotDisturbAltOutlined";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import "./CommonTicket.css";
import TicketDetailsModal from "./TicketModal";
import TIcketDrawer from "./TicketDrawer";
import { getMessageApi, sendMsgApi, ticketStatusApi } from "../../api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useConfirm } from "../confirmDialog/useConfirm";
import InfiniteScroll from "react-infinite-scroll-component";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Picker from "emoji-picker-react";
dayjs.extend(relativeTime);

// ─── helpers ────────────────────────────────────────────────────────────────

function getInitials(title) {
  if (!title) return "RYF";
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "RYF";
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const STATUS_COLORS = {
  Open:     { bg: "#FEE2E2", color: "#DC2626" },
  Closed:   { bg: "#D1FAE5", color: "#059669" },
  Reported: { bg: "#FEF3C7", color: "#D97706" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_COLORS[status] || { bg: "#F3F4F6", color: "#6B7280" };
  return (
    <Box sx={{
      px: 1.25, borderRadius: "20px", flexShrink: 0,
      background: cfg.bg, color: cfg.color,
      fontSize: 10, fontWeight: 700, fontFamily: "Heebo",
      whiteSpace: "nowrap", lineHeight: 2, letterSpacing: "0.3px",
    }}>
      {status || "N/A"}
    </Box>
  );
}

// ─── skeletons ──────────────────────────────────────────────────────────────

function TicketItemSkeleton() {
  return (
    <Box sx={{ px: 2, py: 1.75, borderBottom: "1px solid #FAFAFA", borderLeft: "3px solid transparent" }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Skeleton variant="text" width="55%" height={15} />
            <Skeleton variant="rounded" width={46} height={16} sx={{ borderRadius: "20px" }} />
          </Box>
          <Skeleton variant="text" width="80%" height={13} />
          <Skeleton variant="text" width="30%" height={11} sx={{ mt: 0.4 }} />
        </Box>
      </Box>
    </Box>
  );
}

function MessageSkeleton({ outgoing }) {
  return (
    <Box sx={{ display: "flex", justifyContent: outgoing ? "flex-end" : "flex-start", mb: 1.25 }}>
      <Box sx={{ maxWidth: 360 }}>
        <Skeleton
          variant="rounded"
          width={outgoing ? 220 : 270}
          height={42}
          sx={{ borderRadius: outgoing ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }}
        />
        <Skeleton
          variant="text"
          width={38}
          height={11}
          sx={{ mt: 0.5, ml: outgoing ? "auto" : 0 }}
        />
      </Box>
    </Box>
  );
}

// ─── component ──────────────────────────────────────────────────────────────

const CommonTicket = ({ AllTicket = [], loadMore, hasMore, setAllTicketData, loading: listLoading = false }) => {
  const token    = useSelector((s) => s?.user?.user?.token);
  const confirm  = useConfirm();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open,            setOpen]            = useState(false);
  const [drawerOpen,      setDrawerOpen]      = useState(false);
  const [selectedTicket,  setSelectedTicket]  = useState(null);
  const [activeTicketId,  setActiveTicketId]  = useState(null);
  const [anchorEl,        setAnchorEl]        = useState(null);
  const [sendMessage,     setSendMessage]     = useState("");
  const [messageData,     setMessageData]     = useState([]);
  const [change,          setChange]          = useState(false);
  const [id,              setId]              = useState("");
  const [emojiAnchorEl,   setEmojiAnchorEl]   = useState(null);
  const [search,          setSearch]          = useState("");
  const [isAtBottom,      setIsAtBottom]      = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const chatRef = useRef(null);

  // ── message fetch ──────────────────────────────────────────────────────────

  const fetchMessages = async (ticketId) => {
    try {
      setLoadingMessages(true);
      const res = await getMessageApi({ token, id: ticketId });
      setMessageData(res?.conversation?.messages || []);
      setId(ticketId);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // auto-select first ticket when list loads
  useEffect(() => {
    if (AllTicket?.length > 0 && !selectedTicket) {
      const first = AllTicket[0];
      setActiveTicketId(first._id);
      setSelectedTicket(first);
      fetchMessages(first._id);
    }
  }, [AllTicket, selectedTicket]);

  // poll for new messages every 30 s
  useEffect(() => {
    const iv = setInterval(() => { if (id) fetchMessages(id); }, 30000);
    return () => clearInterval(iv);
  }, [id]);

  // ── scroll helpers ─────────────────────────────────────────────────────────

  const checkBottom = () => {
    const el = chatRef.current;
    if (el) setIsAtBottom(el.scrollHeight - el.scrollTop <= el.clientHeight + 10);
  };
  useEffect(() => {
    if (chatRef.current && isAtBottom) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messageData]);
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkBottom);
    return () => el.removeEventListener("scroll", checkBottom);
  }, []);

  // ── ticket handlers ────────────────────────────────────────────────────────

  const handleTicketSelect = (item) => {
    setActiveTicketId(item._id);
    setSelectedTicket(item);
    fetchMessages(item._id);
    if (isMobile) { setDrawerOpen(true); }
  };

  const handleTicketClose = async (ticketId) => {
    const confirmed = await confirm({
      title: "Close Ticket",
      description: "Are you sure you want to close this ticket?",
      confirmText: "Close Ticket",
    });
    if (!confirmed) return;

    const optimistic = AllTicket.map((t) => t._id === ticketId ? { ...t, status: "Closed" } : t);
    setAllTicketData?.(optimistic);
    if (selectedTicket?._id === ticketId) setSelectedTicket((p) => ({ ...p, status: "Closed" }));

    try {
      const res = await ticketStatusApi({ token, id: ticketId, status: "Closed" });
      toast.success(res?.message || "Ticket closed successfully");
      setChange((p) => !p);
    } catch (err) {
      toast.error(err?.message || "Failed to close ticket");
      const reverted = AllTicket.map((t) => t._id === ticketId ? { ...t, status: "Open" } : t);
      setAllTicketData?.(reverted);
      if (selectedTicket?._id === ticketId) setSelectedTicket((p) => ({ ...p, status: "Open" }));
    }
  };

  const handleSendMsg = async (ticketId) => {
    if (!sendMessage.trim()) return;
    const text = sendMessage;
    setSendMessage("");
    try {
      const res = await sendMsgApi({ token, id: ticketId, message: text });
      setMessageData((p) => [...p, { _id: res?.data?.messageId || Date.now(), message: text }]);
      setChange((p) => !p);
    } catch (err) {
      toast.error(err?.message || err?.error || "Message could not be sent");
    }
  };

  // ── misc ───────────────────────────────────────────────────────────────────

  const formatDate = (date) => {
    if (dayjs().isSame(date, "day")) return "Today";
    if (dayjs().subtract(1, "day").isSame(date, "day")) return "Yesterday";
    return dayjs(date).format("MMMM D, YYYY");
  };

  const filtered = AllTicket.filter((t) =>
    !search ||
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...messageData].sort((a, b) =>
    dayjs(a.updatedAt).isBefore(dayjs(b.updatedAt)) ? -1 : 1
  );

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Box sx={{
        display: "flex",
        height: "calc(100vh - 64px - 24px - 24px - 70px)",
        background: "#fff",
        borderRadius: "24px",
        boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}>

        {/* ── Left panel — ticket list ───────────────────────────────────── */}
        <Box sx={{
          width: 300, minWidth: 300,
          borderRight: "1px solid #F0F0F0",
          display: { xs: isMobile && selectedTicket ? "none" : "flex", sm: "flex" },
          flexDirection: "column",
          overflow: "hidden",
        }}>

          {/* panel header */}
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, borderBottom: "1px solid #F5F5F5" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: 14, color: "#362B24" }}>
                Tickets
              </Typography>
              <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: "#C4C9D4", fontWeight: 500 }}>
                {AllTicket.length}
              </Typography>
            </Box>

            {/* search */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              background: "#F7F8FA", borderRadius: "12px", px: 1.5, py: 0.85,
              border: "1px solid #EFEFEF",
            }}>
              <SearchRoundedIcon sx={{ fontSize: 14, color: "#C4C9D4", flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tickets…"
                style={{
                  border: "none", outline: "none", background: "transparent",
                  fontFamily: "Heebo", fontSize: 12, width: "100%", color: "#362B24",
                }}
              />
            </Box>
          </Box>

          {/* list */}
          <Box
            id="scrollableDiv"
            sx={{
              flex: 1, overflowY: "auto",
              "&::-webkit-scrollbar": { width: 3 },
              "&::-webkit-scrollbar-thumb": { background: "#E5E7EB", borderRadius: 4 },
            }}
          >
            {listLoading ? (
              [0, 1, 2, 3, 4].map((k) => <TicketItemSkeleton key={k} />)
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: "#C4C9D4" }}>
                  No tickets found
                </Typography>
              </Box>
            ) : (
              <InfiniteScroll
                dataLength={AllTicket.length}
                next={loadMore}
                hasMore={hasMore}
                loader={
                  <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF", textAlign: "center", py: 1.5 }}>
                    Loading…
                  </Typography>
                }
                scrollableTarget="scrollableDiv"
              >
                {filtered.map((item) => {
                  const active = activeTicketId === item._id;
                  return (
                    <Box
                      key={item._id}
                      onClick={() => handleTicketSelect(item)}
                      sx={{
                        px: 2, py: 1.75, cursor: "pointer",
                        borderBottom: "1px solid #FAFAFA",
                        borderLeft: active ? "3px solid #065F46" : "3px solid transparent",
                        background: active ? "rgba(6,95,70,0.04)" : "#fff",
                        transition: "all 0.15s ease",
                        "&:hover": { background: active ? "rgba(6,95,70,0.05)" : "#FAFAFA" },
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                        {/* avatar */}
                        <Box sx={{
                          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                          background: "linear-gradient(135deg, #065F46, #0D7B5C)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "Heebo",
                        }}>
                          {getInitials(item.title)}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.4 }}>
                            <Typography sx={{
                              fontFamily: "Heebo", fontWeight: 600, fontSize: 12.5, color: "#362B24",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
                            }}>
                              {item.title || "N/A"}
                            </Typography>
                            <StatusBadge status={item.status} />
                          </Box>
                          <Typography sx={{
                            fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {item.description || "No description"}
                          </Typography>
                          <Typography sx={{ fontFamily: "Heebo", fontSize: 10, color: "#C4C9D4", mt: 0.5 }}>
                            {item.createdAt ? dayjs(item.createdAt).fromNow() : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </InfiniteScroll>
            )}
          </Box>
        </Box>

        {/* ── Right panel — conversation ─────────────────────────────────── */}
        {selectedTicket ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

            {/* chat header */}
            <Box sx={{
              px: 3, py: 2,
              borderBottom: "1px solid #F0F0F0",
              display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg, #065F46, #0D7B5C)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "Heebo",
                }}>
                  {getInitials(selectedTicket.title)}
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontFamily: "Heebo", fontWeight: 700, fontSize: 14, color: "#362B24" }}>
                      {selectedTicket.title || "N/A"}
                    </Typography>
                    <StatusBadge status={selectedTicket.status} />
                  </Box>
                  <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF" }}>
                    #{selectedTicket.ticketNumber || "—"}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ color: "#C4C9D4", "&:hover": { color: "#362B24", background: "#F7F8FA" } }}
              >
                <MoreVertRoundedIcon fontSize="small" />
              </IconButton>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    borderRadius: "18px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                    overflow: "hidden",
                    mt: 0.75,
                    minWidth: 210,
                    border: "1px solid #F0F0F0",
                  },
                }}
              >
                {/* View Details */}
                <Box
                  onClick={() => { setOpen(true); setAnchorEl(null); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2, py: 1.5, cursor: "pointer",
                    transition: "background 0.15s",
                    "&:hover": { background: "#F7F8FA" },
                  }}
                >
                  <Box sx={{
                    width: 32, height: 32, borderRadius: "10px",
                    background: "rgba(6,95,70,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "#065F46" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "Heebo", fontSize: 13, fontWeight: 600, color: "#362B24", lineHeight: 1.3 }}>
                      View Details
                    </Typography>
                    <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF", lineHeight: 1.3 }}>
                      See full ticket info
                    </Typography>
                  </Box>
                </Box>

                {selectedTicket?.status !== "Closed" && (
                  <>
                    <Divider sx={{ borderColor: "#F5F5F5" }} />
                    <Box
                      onClick={() => { handleTicketClose(selectedTicket._id); setAnchorEl(null); }}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 2, py: 1.5, cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": { background: "#FEF2F2" },
                      }}
                    >
                      <Box sx={{
                        width: 32, height: 32, borderRadius: "10px",
                        background: "rgba(220,38,38,0.08)",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <DoNotDisturbAltOutlinedIcon sx={{ fontSize: 16, color: "#DC2626" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontFamily: "Heebo", fontSize: 13, fontWeight: 600, color: "#DC2626", lineHeight: 1.3 }}>
                          Close Ticket
                        </Typography>
                        <Typography sx={{ fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF", lineHeight: 1.3 }}>
                          Mark as resolved
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Popover>
            </Box>

            {/* messages */}
            <Box
              ref={chatRef}
              sx={{
                flex: 1, overflowY: "auto", px: 3, py: 2.5,
                display: "flex", flexDirection: "column",
                "&::-webkit-scrollbar": { width: 3 },
                "&::-webkit-scrollbar-thumb": { background: "#E5E7EB", borderRadius: 4 },
              }}
            >
              {loadingMessages ? (
                <>
                  <MessageSkeleton outgoing={false} />
                  <MessageSkeleton outgoing={true} />
                  <MessageSkeleton outgoing={false} />
                  <MessageSkeleton outgoing={true} />
                  <MessageSkeleton outgoing={false} />
                </>
              ) : sorted.length === 0 ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: "#C4C9D4" }}>
                    No messages yet
                  </Typography>
                </Box>
              ) : (
                sorted.map((msg, i) => {
                  const showDate =
                    i === 0 ||
                    !dayjs(sorted[i - 1]?.updatedAt).isSame(msg.updatedAt, "day");

                  return (
                    <Fragment key={msg._id}>
                      {showDate && (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                          <Typography sx={{
                            fontFamily: "Heebo", fontSize: 11, color: "#9CA3AF",
                            background: "#F3F4F6", px: 1.5, py: 0.4, borderRadius: "20px",
                          }}>
                            {formatDate(msg.updatedAt)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", justifyContent: msg.sender ? "flex-start" : "flex-end", mb: 1.25 }}>
                        <Box>
                          <Box sx={{
                            maxWidth: 380, px: 2, py: 1.25,
                            borderRadius: msg.sender ? "18px 18px 18px 4px" : "18px 18px 4px 18px",
                            background: msg.sender
                              ? "#F3F4F6"
                              : "linear-gradient(135deg, #065F46 0%, #0D7B5C 100%)",
                            color: msg.sender ? "#362B24" : "#fff",
                            fontFamily: "Heebo", fontSize: 13, lineHeight: 1.55, wordBreak: "break-word",
                          }}>
                            {msg.message}
                          </Box>
                          <Typography sx={{
                            fontFamily: "Heebo", fontSize: 10, color: "#C4C9D4", mt: 0.5,
                            textAlign: msg.sender ? "left" : "right",
                          }}>
                            {dayjs(msg.updatedAt).format("h:mm A")}
                          </Typography>
                        </Box>
                      </Box>
                    </Fragment>
                  );
                })
              )}
            </Box>

            {/* input */}
            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #F0F0F0", flexShrink: 0, position: "relative" }}>
              <Popover
                open={Boolean(emojiAnchorEl)}
                anchorEl={emojiAnchorEl}
                onClose={() => setEmojiAnchorEl(null)}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                disableRestoreFocus
                PaperProps={{ sx: { boxShadow: "0 8px 32px rgba(0,0,0,0.12)", borderRadius: "16px", overflow: "hidden" } }}
              >
                <Picker onEmojiClick={(e) => { setSendMessage((p) => p + e.emoji); setEmojiAnchorEl(null); }} />
              </Popover>

              {selectedTicket.status === "Open" ? (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  background: "#F7F8FA", borderRadius: "16px", px: 2, py: 1,
                  border: "1px solid #EFEFEF",
                }}>
                  <EmojiEmotionsOutlinedIcon
                    sx={{ color: "#C4C9D4", cursor: "pointer", fontSize: 20, flexShrink: 0, transition: "color 0.15s", "&:hover": { color: "#065F46" } }}
                    onClick={(e) => setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget)}
                  />
                  <input
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) handleSendMsg(selectedTicket._id); }}
                    placeholder="Type a message…"
                    style={{
                      flex: 1, border: "none", outline: "none", background: "transparent",
                      fontFamily: "Heebo", fontSize: 13, color: "#362B24",
                    }}
                  />
                  <Box
                    onClick={() => handleSendMsg(selectedTicket._id)}
                    sx={{
                      width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
                      background: sendMessage.trim()
                        ? "linear-gradient(135deg, #065F46, #0D7B5C)"
                        : "#EBEBEB",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: sendMessage.trim() ? "pointer" : "default",
                      transition: "background 0.2s ease",
                    }}
                  >
                    <SendRoundedIcon sx={{ color: sendMessage.trim() ? "#fff" : "#C4C9D4", fontSize: 16 }} />
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: "#C4C9D4", textAlign: "center", py: 0.5 }}>
                  This ticket is closed — no further replies can be sent.
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          /* empty state */
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <Box sx={{
              width: 60, height: 60, borderRadius: "18px",
              background: "rgba(6,95,70,0.07)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <HeadsetMicOutlinedIcon sx={{ fontSize: 30, color: "#065F46" }} />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontFamily: "Heebo", fontWeight: 600, fontSize: 14, color: "#362B24", mb: 0.5 }}>
                No ticket selected
              </Typography>
              <Typography sx={{ fontFamily: "Heebo", fontSize: 12, color: "#9CA3AF" }}>
                Pick a ticket from the list to view the conversation
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <TicketDetailsModal open={open} handleClose={() => setOpen(false)} selectedTicket={selectedTicket} />

      <TIcketDrawer
        drawerOpen={drawerOpen}
        isBelowMd={isMobile}
        handleDrawerClose={() => setDrawerOpen(false)}
        selectedTicket={selectedTicket}
        messsageData={messageData}
        handleOpen={() => setOpen(true)}
        onCloseTicket={handleTicketClose}
        dateFormate={formatDate}
        sendMessage={sendMessage}
        setSendMessage={setSendMessage}
        handleSendMsg={handleSendMsg}
        chatRef={chatRef}
      />
    </>
  );
};

export default CommonTicket;
